import React, { useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { autorun, toJS, runInAction } from 'mobx';
import bbox from '@turf/bbox';
import { featureCollection, lineString, point, combine } from '@vromo/geojson';
import { useLocalObservable } from 'mobx-react-lite';
import { observer } from 'mobx-react-lite';
import useStylesheets from '@vromo/hooks/useStylesheets';
import mapboxgl from 'mapbox-gl';
import some from 'lodash/some';
import FontAwesome from '@vromo/ui/components/FontAwesome';

import MapboxStyle from 'mapbox-gl/dist/mapbox-gl.css';
import { EntityList, Job, Vehicle } from '@vromo/models';
import { mapboxStyles, secrets } from '@vromo/api';

const Container = styled.div`
    flex: 1;
    background-color: white;
    position: relative;
    width: ${({ width }) => width}px;
    height: ${({ height }) => height}px;
`;

const DEFAULT_SOURCE = {
    type: 'geojson',
    data: {
        type: 'FeatureCollection',
        features: []
    }
};

interface Map2Props {
    jobs: EntityList<Job>;
    vehicles: EntityList<Vehicle>;
    width: number;
    height: number;
}

export default observer<Map2Props>(function Map2({ jobs, vehicles, width, height }) {
    useStylesheets(MapboxStyle);

    const mapRef = useRef(null);
    const mapContainerRef = useRef(null);
    const state = useLocalObservable(() => ({ tracking: true }));

    function fitBounds(linear = false, userDidntInteract = false) {
        const map = mapRef.current;

        const bounds = bbox(
            combine(
                map.getSource('jobs')._data,
                map.getSource('vehicles')._data,
                map.getSource('routes')._data
            )
        );

        if (!some(bounds, (it) => it === Infinity)) {
            map.fitBounds(
                bounds,
                {
                    linear,
                    padding: 40
                },
                {
                    userDidntInteract
                }
            );
        }
    }

    useEffect(() => {
        if (mapRef.current) mapRef.current.resize();
    }, [width, height]);

    useEffect(() => {
        mapboxgl.accessToken = secrets.mapbox;


        const map = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: mapboxStyles.light
        });

        mapRef.current = map;

        map.on('load', () => {
            map.on('move', (e) => {
                if (!e.userDidntInteract && state.tracking) {
                    runInAction(() => (state.tracking = false));
                }
            });

            map.addSource('routes', DEFAULT_SOURCE);
            map.addSource('jobs', DEFAULT_SOURCE);
            map.addSource('vehicles', DEFAULT_SOURCE);

            map.addLayer({
                id: 'routes-layer',
                type: 'line',
                source: 'routes',
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round'
                },
                paint: {
                    'line-color': '#5090FA',
                    'line-width': 4,
                    'line-opacity': 0.8
                }
            });

            map.addLayer({
                id: 'jobs-layer',
                type: 'symbol',
                source: 'jobs',
                layout: {
                    'icon-image': '{icon}',
                    'icon-size': 0.5,
                    'icon-allow-overlap': true,
                    'text-field': '{eta}',
                    'text-anchor': 'left',
                    'text-offset': [1.2, 0],
                    'text-size': 12,
                    'text-optional': true
                }
            });

            map.addLayer({
                id: 'vehicles-layer',
                type: 'symbol',
                source: 'vehicles',
                layout: {
                    'icon-image': '{icon}',
                    'icon-size': 0.4,
                    'icon-rotate': ['number', ['get', 'heading'], 0],
                    'icon-allow-overlap': true
                }
            });

            map.loadImage(require('@vromo/icons/pickup/icon.png'), (err, image) => {
                map.addImage('pickup', image);
            });

            map.loadImage(require('@vromo/icons/dropoff/icon.png'), (err, image) => {
                map.addImage('dropoff', image);
            });

            map.loadImage(require('@vromo/icons/car/highlighted.png'), (err, image) => {
                map.addImage('vehicle', image);
            });

            return autorun(() => {
                map.getSource('routes').setData(
                    featureCollection(
                        jobs
                            .flatMap((it) => it.route.slice(1).map((it) => it))
                            .map((it) =>
                                lineString(it.polyline.decoded.map(({ lon, lat }) => [lon, lat]))
                            )
                    )
                );

                map.getSource('vehicles').setData(
                    featureCollection(
                        vehicles.map((it) =>
                            point([it.location.lon, it.location.lat], {
                                icon: 'vehicle',
                                heading: it.point.heading
                            })
                        )
                    )
                );

                map.getSource('jobs').setData(
                    featureCollection(
                        jobs
                            .flatMap((it) => toJS(it.tasks.items))
                            .map((it, ix) =>
                                point([it.zone.coords.lon, it.zone.coords.lat], {
                                    icon: ix === 0 ? 'pickup' : 'dropoff'
                                })
                            )
                    )
                );

                if (state.tracking) fitBounds(true, true);
            });
        });
    }, []);

    return (
        <Container width={width} height={height}>
            <div ref={mapContainerRef} style={{ width, height }} />
            {state.tracking ? null : (
                <div
                    style={{
                        padding: 6,
                        borderRadius: '50%',
                        lineHeight: 1,
                        backgroundColor: 'black',
                        color: 'white',
                        cursor: 'pointer',
                        position: 'absolute',
                        top: 10,
                        right: 10
                    }}
                    onClick={() => {
                        fitBounds(false, true);
                        runInAction(() => {
                            state.tracking = true;
                        });
                    }}>
                    <FontAwesome icon="crosshairs" />
                </div>
            )}
        </Container>
    );
});
